import { useCallback } from 'react'
import { NavigateOptions, useNavigate } from 'react-router-dom'

import { isV2SupportDeprecatedPath, navigateToV3, V2_SUPPORT_DEPRECATED_FLAG } from '@/constants/featureFlags'
import { useFeatureFlagsContext } from '@/context'

/**
 * Wraps react-router's `useNavigate` for path-string navigation (does not
 * support the numeric-delta or `Partial<Path>` overloads). When
 * `v2SupportDeprecated` is on, navigating to a path under the deprecated V2
 * "support" surface (tickets/members/reservations/homes/properties/jobs)
 * sends the browser to Mission Control V3 instead — the same rule
 * SupportRouter enforces at the route level, applied here so links elsewhere
 * in the app don't dead-end.
 *
 * V3 is a different origin, so this leaves the SPA entirely via
 * `window.location` rather than `navigate`. `assign` (not `replace`) keeps V2
 * in history, so the browser back button still returns here.
 */
export function useGuardedNavigate() {
  const navigate = useNavigate()
  const { isEnabled } = useFeatureFlagsContext()
  const supportDeprecated = isEnabled(V2_SUPPORT_DEPRECATED_FLAG)

  return useCallback(
    (path: string, options?: NavigateOptions) => {
      if (supportDeprecated && isV2SupportDeprecatedPath(path)) {
        navigateToV3()
        return
      }
      navigate(path, options)
    },
    [navigate, supportDeprecated],
  )
}
